import { useContext, createContext, useState, useEffect, useCallback, useRef } from "react";
import { auth, db, getUserPreferences, updateUserPreferences } from "../lib/firebase/firebase";
import { doc, updateDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import usePremiumStatus from "../lib/stripe/usePremiumStatus";
import useEmbroideryPremiumStatus from "../lib/stripe/useEmbroideryPremiumStatus";
import RevenueCatService from "../lib/revenuecat/RevenueCatService";
import { isNativeiOS, isNativeAndroid } from "../lib/utils/platform";
import { isEmbroideryTrace } from "../config/appVariant";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, loading] = useAuthState(auth);
  const [userIsPremium, loadingStatus, refreshPremiumStatus, justSubscribed, clearJustSubscribed] = usePremiumStatus(user);
  const [userIsEmbroideryPremium, refreshEmbroideryPremiumStatus, embroideryLoading] = useEmbroideryPremiumStatus(isEmbroideryTrace ? user : null);
  const [userPreferences, setUserPreferences] = useState({});
  const [preferencesLoading, setPreferencesLoading] = useState(true); // Start as true to wait for initial load
  
  const isLoading = loading || loadingStatus || (isEmbroideryTrace && embroideryLoading) || (user && userIsPremium === null);

  const rcInitRef = useRef(false);

  // Initialize or tear down RevenueCat on iOS when auth state changes
  useEffect(() => {
    if (!isNativeiOS()) return;

    const rc = RevenueCatService.getInstance();

    if (user?.uid && !rcInitRef.current) {
      rcInitRef.current = true;
      rc.initialize(user.uid).catch((err) => {
        console.error("RevenueCat init error:", err);
        rcInitRef.current = false;
      });
    } else if (!user && rcInitRef.current) {
      rcInitRef.current = false;
      rc.logout();
    }
  }, [user?.uid]);

  // Track user activity — debounced, max once per 6 hours.
  // Also records which app variant (svgtrace / embroiderytrace) the user
  // is currently using so the email system can send correctly branded messages.
  const lastActivityUpdateRef = useRef(0);
  useEffect(() => {
    if (!user?.uid) return;
    const now = Date.now();
    const SIX_HOURS = 6 * 60 * 60 * 1000;
    if (now - lastActivityUpdateRef.current < SIX_HOURS) return;
    lastActivityUpdateRef.current = now;

    const source = isNativeiOS() ? "ios" : isNativeAndroid() ? "android" : "web";
    const currentApp = isEmbroideryTrace ? "embroiderytrace" : "svgtrace";
    const userRef = doc(db, "users", user.uid);
    updateDoc(userRef, {
      lastActiveAt: serverTimestamp(),
      lastActiveSource: source,
      lastActiveApp: currentApp,
      apps: arrayUnion(currentApp),
    }).catch(() => {});
  }, [user?.uid]);

  // Fetch user preferences when user changes
  useEffect(() => {
    const fetchPreferences = async () => {
      if (user?.uid) {
        setPreferencesLoading(true);
        try {
          const prefs = await getUserPreferences(user.uid);
          setUserPreferences(prefs);
        } catch (error) {
          console.error("Error fetching user preferences:", error);
        } finally {
          setPreferencesLoading(false);
        }
      } else {
        setUserPreferences({});
        setPreferencesLoading(false);
      }
    };

    fetchPreferences();
  }, [user?.uid]);

  // Function to update preferences (both local state and Firestore)
  const updatePreferences = useCallback(async (newPreferences) => {
    if (!user?.uid) return false;
    
    // Optimistically update local state
    setUserPreferences(prev => ({ ...prev, ...newPreferences }));
    
    // Update in Firestore
    const success = await updateUserPreferences(user.uid, newPreferences);
    
    if (!success) {
      // Revert on failure
      const prefs = await getUserPreferences(user.uid);
      setUserPreferences(prefs);
    }
    
    return success;
  }, [user?.uid]);

  const userHasAIAccess = isEmbroideryTrace ? !!userIsEmbroideryPremium : !!userIsPremium;

  const value = {
    user,
    userIsPremium,
    userIsEmbroideryPremium,
    userHasAIAccess,
    refreshPremiumStatus,
    refreshEmbroideryPremiumStatus,
    justSubscribed,
    clearJustSubscribed,
    userPreferences,
    preferencesLoading,
    updatePreferences,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
